import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { PRESET_THEMES, getThemeById } from "@/lib/presets";
import { realEventId } from "@/lib/timeUtils";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [customThemes, setCustomThemes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [sharedEvents, setSharedEvents] = useState([]);
  const [outgoingShares, setOutgoingShares] = useState([]);
  const [incomingShares, setIncomingShares] = useState([]);
  const [ready, setReady] = useState(false);
  const previewRef = useRef(null);

  const loadAll = useCallback(async () => {
    const [ev, tk, cat, cal, th, st, shev, sho, shi] = await Promise.all([
      api.get("/events"), api.get("/tasks"), api.get("/categories"),
      api.get("/calendars"), api.get("/themes"), api.get("/settings"),
      api.get("/shared-events"), api.get("/shares/outgoing"), api.get("/shares/incoming"),
    ]);
    setEvents(ev.data); setTasks(tk.data); setCategories(cat.data);
    setCalendars(cal.data); setCustomThemes(th.data); setSettings(st.data);
    setSharedEvents(shev.data); setOutgoingShares(sho.data); setIncomingShares(shi.data);
    setReady(true);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Apply active theme variables to :root
  const applyTheme = useCallback((variables, type) => {
    const root = document.documentElement;
    Object.entries(variables).forEach(([k, v]) => root.style.setProperty(k, v));
    document.body.dataset.themeType = type || "dark";
  }, []);

  useEffect(() => {
    if (!settings) return;
    if (previewRef.current) return; // don't override active live preview
    const theme = getThemeById(settings.active_theme_id, customThemes);
    applyTheme(theme.variables, theme.type);
    document.documentElement.style.setProperty("--font-scale", settings.font_scale || 1);
  }, [settings, customThemes, applyTheme]);

  const setPreview = (variables, type) => {
    previewRef.current = variables ? true : null;
    if (variables) applyTheme(variables, type);
    else if (settings) {
      const theme = getThemeById(settings.active_theme_id, customThemes);
      applyTheme(theme.variables, theme.type);
    }
  };

  // ---- CRUD helpers ----
  const crud = (setter, path) => ({
    add: async (payload) => { const r = await api.post(`/${path}`, payload); setter((p) => [...p, r.data]); return r.data; },
    update: async (id, payload) => { const r = await api.put(`/${path}/${realEventId(id)}`, payload); setter((p) => p.map((x) => (x.id === realEventId(id) ? r.data : x))); return r.data; },
    remove: async (id) => { await api.delete(`/${path}/${realEventId(id)}`); setter((p) => p.filter((x) => x.id !== realEventId(id))); },
  });

  const eventsApi = {
    ...crud(setEvents, "events"),
    addBulk: async (list) => {
      const r = await api.post("/events/bulk", { events: list });
      setEvents((p) => [...p, ...r.data]);
      return r.data;
    },
  };
  const tasksApi = crud(setTasks, "tasks");
  const categoriesApi = crud(setCategories, "categories");
  const calendarsApi = crud(setCalendars, "calendars");
  const themesApi = crud(setCustomThemes, "themes");

  const updateSettings = async (payload) => {
    const r = await api.put("/settings", payload);
    setSettings(r.data);
    return r.data;
  };

  const reloadShares = useCallback(async () => {
    const [shev, sho, shi] = await Promise.all([
      api.get("/shared-events"), api.get("/shares/outgoing"), api.get("/shares/incoming"),
    ]);
    setSharedEvents(shev.data); setOutgoingShares(sho.data); setIncomingShares(shi.data);
  }, []);

  const sharesApi = {
    create: async (recipient_email, permission, color) => { await api.post("/shares", { recipient_email, permission, color }); await reloadShares(); },
    update: async (id, patch) => { await api.put(`/shares/${id}`, patch); await reloadShares(); },
    remove: async (id) => { await api.delete(`/shares/${id}`); await reloadShares(); },
  };

  const getCategory = (id) => categories.find((c) => c.id === id);
  const eventColor = (ev) => ev.color || getCategory(ev.category_id)?.color || "var(--accent-primary)";

  const value = {
    events, tasks, categories, calendars, customThemes, settings, ready,
    sharedEvents, outgoingShares, incomingShares, sharesApi, reloadShares,
    presetThemes: PRESET_THEMES,
    eventsApi, tasksApi, categoriesApi, calendarsApi, themesApi,
    updateSettings, applyTheme, setPreview,
    getCategory, eventColor, reload: loadAll,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  return useContext(AppContext);
}
